import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";
import { fetchFormConfigsWithMeta } from "../api/formConfigApi";

interface FormConfigGuardProps {
    children: ReactNode;
    silent?: boolean;
}

export const FormConfigGuard = ({ children, silent = false }: FormConfigGuardProps) => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.admin || user?.info?.admin;
    const [hasAccess, setHasAccess] = useState<boolean | null>(null);

    useEffect(() => {
        if (loading || isAdmin) {
            return;
        }

        let cancelled = false;
        fetchFormConfigsWithMeta()
            .then((result) => {
                if (!cancelled) {
                    setHasAccess(result.meta?.formConfigAccess === true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setHasAccess(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [loading, isAdmin]);

    useEffect(() => {
        if (loading || isAdmin || hasAccess === null || hasAccess || silent) {
            return;
        }
        toast.warn("Form configuration access required.", { autoClose: 5000 });
        navigate("/", { replace: true });
    }, [loading, isAdmin, hasAccess, silent, navigate]);

    if (loading || (!isAdmin && hasAccess === null)) {
        return null;
    }

    if (isAdmin || hasAccess) {
        return <>{children}</>;
    }

    if (silent) {
        return null;
    }

    return null;
};
